import classNames from 'classnames';

import React, {
  AllHTMLAttributes,
  CSSProperties,
  FC,
  NamedExoticComponent,
  ReactElement,
  ReactNode,
} from 'react';

import { Typography } from '../typography';

import styles from './segmented-item.scss';

type Props = {
  children?: ReactNode &
    ReactElement<Pick<AllHTMLAttributes<SVGElement>, 'name'>, NamedExoticComponent>;
  value: string;
  small?: boolean;
  checked?: boolean;
  className?: string;
  style?: CSSProperties;
  onChange?: (value: string) => void;
  divider?: boolean;
  dataTestId?: string;
};

const COMPONENT_ID = 'modulor-segmented-item';

export const SegmentedItem: FC<Props> = ({
  children,
  value,
  small = false,
  checked = false,
  divider = false,
  className,
  style,
  onChange,
  dataTestId = COMPONENT_ID,
}: Props) => {
  const classes = classNames(styles.container, className, {
    [styles.active]: checked,
    [styles.small]: small,
    [styles.divider]: divider,
  });
  const labelVariant = () => {
    if (small) {
      return checked ? 'subhead-semibold' : 'subhead-regular';
    }
    return checked ? 'body-semibold' : 'body-regular';
  };

  const handleChange = () => {
    onChange?.(value);
  };

  const replaceIconName = (name: string) => {
    if (checked && name.includes('outlined')) {
      return name.replace('outlined', 'filled');
    } else if (!checked && name.includes('filled')) {
      return name.replace('filled', 'outlined');
    }
    return name;
  };

  const getChildrenAndSetIconName = () => {
    if (typeof children === 'object' && children?.type?.displayName === 'Icon') {
      return {
        ...children,
        props: {
          ...children.props,
          name: replaceIconName(children?.props?.name),
        },
      };
    }
    return children;
  };

  return (
    <div
      data-testid={dataTestId}
      data-id={dataTestId}
      data-component-name={COMPONENT_ID}
      className={classes}
      style={style}>
      <label>
        <input
          type="radio"
          name="radio"
          value={value}
          checked={checked}
          onChange={handleChange}
          data-component-name={`${dataTestId}-input`}
        />
        <Typography tag="p" variant={labelVariant()} dataTestId={`${dataTestId}-label`}>
          {getChildrenAndSetIconName()}
        </Typography>
      </label>
    </div>
  );
};
